/**
 * Autocomplete dropdown for ingredient name cells.
 *
 * Queries /api/ingredients?q=… on every keystroke (debounced lightly via
 * React-state batching). On select, fills several sibling cells (INCI,
 * supplier, phase) from the library entry.
 */

import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { searchIngredients } from '../api/ingredients';
import type { IngredientLibraryItem } from '../types';

interface Props {
  value: string;
  onChange: (next: string) => void;
  onSelect: (item: IngredientLibraryItem) => void;
  onFocus: () => void;
  onBlur: () => void;
  placeholder?: string;
  className?: string;
}

export default function IngredientAutocomplete({
  value,
  onChange,
  onSelect,
  onFocus,
  onBlur,
  placeholder,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<IngredientLibraryItem[]>([]);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const q = value.trim();
    const handle = setTimeout(async () => {
      try {
        const { items } = await searchIngredients(q, 10);
        if (!cancelled) {
          setItems(items);
          setHighlight(0);
        }
      } catch {
        if (!cancelled) setItems([]);
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [value, open]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function pick(item: IngredientLibraryItem) {
    onSelect(item);
    setOpen(false);
  }

  return (
    <div className={'ingredient-cell ' + (className ?? '')} ref={boxRef}>
      <input
        className="cell-in"
        value={value}
        placeholder={placeholder ?? 'Ingredient name'}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          onFocus();
        }}
        onBlur={() => {
          // delay to allow click on item
          setTimeout(() => {
            onBlur();
          }, 100);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(items.length - 1, h + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            if (items[highlight]) {
              e.preventDefault();
              pick(items[highlight]!);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && items.length > 0 && (
        <div className="autocomplete-pop">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className={'autocomplete-item' + (idx === highlight ? ' highlighted' : '')}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(it);
              }}
              onMouseEnter={() => setHighlight(idx)}
            >
              <div>{it.name}</div>
              <div className="autocomplete-meta">
                {it.inci && <span>{it.inci}</span>}
                {it.default_phase && <span>· {it.default_phase}</span>}
                {it.function && <span>· {it.function}</span>}
                {it.usage_count > 0 && <span>· used {it.usage_count}×</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && value.trim() && items.length === 0 && (
        <div className="autocomplete-pop">
          <div className="autocomplete-empty">No matches — keep typing to add new</div>
        </div>
      )}
    </div>
  );
}
